import styled, { css } from 'styled-components';
import { KarStatusBanner } from '@kar-dmp/pattern-library/react';
import React from 'react';
import { TradeState } from '../../../_shared/types/trade';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';
import { BidStatus, BidStatusName } from '../../../_shared/types/auction';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

const BidBoxBorderVariants = {
  [BidStatusName[BidStatus.topBidder]]: css`
    outline: 2px solid ${({ theme }) => theme.COLORS.LA_PALMA};
  `,
  [BidStatusName[BidStatus.outbid]]: css`
    outline: 2px solid ${({ theme }) => theme.COLORS.MONZA};
  `
};

/**
 * Variant Definitions
 */
const StyledBidBoxVariants = {
  [TradeState.upcoming]: css`
    flex-direction: row;
    align-items: center;
  `,
  mini: css`
    padding: 18px;
    position: ${isOmp ? 'absolute' : 'fixed'};
    bottom: 0;
    width: 100%;
    max-width: 516px;
    min-width: 496px;
    height: max-content;
    justify-content: space-between;
    ${({ tradeState }) => StyledBidBoxVariants[tradeState]}
    z-index: 3;
    box-shadow: ${isOmp ? 'none' : '0 1px 8px rgba(0, 0, 0, 0.2)'};
    border-top: ${({ theme }) =>
      isOmp ? `1px solid ${theme.COLORS.NEUTRAL_110}` : 'none'};
  `,
  expanded: css`
    min-height: ${({ isSelling, tradeState }) => {
      const base = '547px';
      if (!isSelling) {
        return base;
      }
      switch (tradeState) {
        case TradeState.closing:
        case TradeState.pending:
          return 'auto';
        default:
          return base;
      }
    }};
    width: 275px;
    ${({ theme }) =>
      isOmp
        ? css`
            border: 1px solid ${theme.COLORS.NEUTRAL_110};
          `
        : css`
            box-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
          `}
    ${({ variant, tradeState }) =>
      [TradeState.pending].includes(tradeState)
        ? null
        : BidBoxBorderVariants[variant]}
  `
};

const StyledMiniVdpBiddingSectionVariants = {
  [TradeState.active]: css`
    border-top: 2px solid ${({ theme }) => theme.COLORS.NEUTRAL_50};
  `,
  top_bidder: css`
    border-top: 2px solid ${({ theme }) => theme.COLORS.LA_PALMA};
  `,
  outbid: css`
    border-top: 2px solid ${({ theme }) => theme.COLORS.MONZA};
  `
};

const StyledContentVariants = {
  mini: css`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    flex-flow: row;
    padding-bottom: ${({ tradeState }) =>
      tradeState === TradeState.active ? 12 : 0}px;
  `,
  [TradeState.pending]: css`
    height: 100%;
  `,
  [TradeState.closing]: css`
    height: 100%;
    flex: 1;
    justify-content: space-between;
  `
};

const StyledBiddingSectionVariants = {
  mini: css`
    display: flex;
    flex-wrap: ${({ isMultiOrg }) => (isMultiOrg ? 'wrap' : 'nowrap')};
    flex-direction: ${({ isMultiOrg }) => (isMultiOrg ? 'column' : 'row')};
    padding-top: ${({ isAuctionEnded, tradeState }) =>
      isAuctionEnded || tradeState !== TradeState.active ? 0 : 18}px;
    ${({ tradeState }) => StyledMiniVdpBiddingSectionVariants[tradeState]}
    ${({ variant, tradeState }) =>
      [TradeState.pending].includes(tradeState)
        ? null
        : StyledMiniVdpBiddingSectionVariants[variant]}
  `
};

/**
 * Styled Components
 */
const StyledBidBox = styled.div`
  background-color: ${({ theme }) => theme.COLORS.BASE};
  display: flex;
  flex-direction: column;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBidBoxVariants.mini,
      expanded: StyledBidBoxVariants.expanded
    })}
  ${({ localeVariant }) => StyledBidBoxVariants[localeVariant]}
`;

export const StyledFadeBorder = styled.div`
  height: 22px;
  width: 100%;
  position: absolute;
  top: -22px;
  left: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.12) 0%,
    rgba(0, 0, 0, 0) 100%
  );
  transform: rotate(-180deg);
  z-index: 10000;
`;

export const StyledContent = styled.div`
  ${({ tradeState }) => StyledContentVariants[tradeState]}
  display: flex;
  flex-flow: column;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledContentVariants.mini
    })}
`;

export const StyledBiddingSection = styled.div`
  ${({ theme, isMultiOrg }) =>
    applyResponsiveVdpStyles(theme, {
      mini: isMultiOrg ? StyledBiddingSectionVariants.mini : null
    })}
  width: 100%;
  margin-top: ${({ tradeState }) =>
    tradeState === TradeState.pending ? ' auto' : 'inherit'};
`;

export const StyledBiddingDisclaimer = styled.div`
  font-style: normal;
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.XSMALL};
  line-height: 16px;
  text-align: center;
  color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
  padding: 3px 12px 12px;
`;

export const StyledKarStatusBanner = styled(props => (
  <KarStatusBanner {...props} />
))`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  font-weight: ${({ theme }) => theme.FONT_WEIGHTS.REGULAR};
  line-height: ${({ theme }) => theme.FONT.SIZE.XLARGE};
  color: ${({ theme }) => theme.COLORS.BLACK};
  width: 100%;
`;

export const DealerNoPaymentMethodContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StyledAddPaymentMethodLink = styled.a`
  cursor: pointer;
  color: #1a47e8;
  text-decoration: none;
`;

export const DealershipSelectionContainer = styled.div`
  margin: 18px 0;
`;

const StyledBidControlsContainerVariants = css`
  width: 100%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
`;

export const BidControlsContainer = styled.div`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBidControlsContainerVariants
    })}
`;

export default StyledBidBox;
